import TaskHeader from "components/task/header/TaskHeader";
import { Box, Button, CircularProgress, Stack } from "@mui/joy";
import { useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import { deviceAtom } from "recoil/deviceAtom";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import React, { useEffect, useState } from "react";
import {
  NOTIFICATION_SETTINGS_DETAIL_ENDPOINT,
  NOTIFICATION_SETTINGS_DOMAIN,
  notificationSettingsApi,
  NotificationSettingsDetailType,
  NotificationSettingsType,
} from "../../api/notificationSettingsApi";
import { AddIcon } from "../../static/image/common/IconAssets";
import { RightArrowSvg } from "../../static/image/common/SvgAssets";
import { useNavigate } from "react-router-dom";
import useIsInappWebview from "hooks/useIsInappWebview";
import RequestPushNotificationButtonInSettings from "../../components/common/RequestPushNotificationButtonInSettings";
import { sendMessage } from "../../utils/webview";
import { useQuery } from "@tanstack/react-query";
import ContentTemplateV2 from "../../components/task/content/ContentTemplateV2";

// Common button styling
const addButtonStyle = {
  minWidth: 0,
  paddingInline: 0,
  "--Button-gap": "4px",
};

// Helper function to format time (e.g., "08:00" to "8:00 AM")
const formatTime = (time: string) => {
  const [hours, minutes] = time.split(":");
  const hour = parseInt(hours, 10);
  const period = hour >= 12 ? "PM" : "AM";
  const formattedHour = hour % 12 || 12;
  return `${formattedHour}:${minutes} ${period}`;
};

// Helper function to format days of week
const formatDaysOfWeek = (dayOfWeekList: number[]) => {
  if (dayOfWeekList.length === 0) {
    return "꺼짐";
  }

  if (dayOfWeekList.length === 7) {
    return "매일";
  }

  const dayMap: Record<number, string> = {
    1: "월",
    2: "화",
    3: "수",
    4: "목",
    5: "금",
    6: "토",
    7: "일",
  };

  return dayOfWeekList.map((day) => dayMap[day]).join(", ");
};

// Reusable component for notification sections
interface NotificationSectionProps {
  title: string;
  notifications?: NotificationSettingsType[];
  emptyMessage: string;
  keyPrefix: string;
  type: "thoughtRecord" | "meditationRecord";
  navigate: (path: string, options?: any) => void;
}

const NotificationSection: React.FC<NotificationSectionProps> = ({
  title,
  notifications,
  emptyMessage,
  keyPrefix,
  type,
  navigate,
}) => {
  // 알림 항목 클릭 시 수정 페이지로 이동
  const handleNotificationClick = (
    notification: NotificationSettingsType,
    index: number
  ) => {
    navigate("/notification-settings-update", {
      state: {
        type,
        index,
        notification,
      },
    });
  };

  // 추가 버튼 클릭 시 새 알림 페이지로 이동
  const handleAddClick = () => {
    navigate("/notification-settings-update", {
      state: {
        type,
      },
    });
  };

  return (
    <Stack spacing={1} key={keyPrefix} id={keyPrefix}>
      <Stack px={1}>
        <CustomTypography
          variant={"Caption"}
          sx={{ color: theme.vars.palette.Content.Medium, fontWeight: 700 }}
        >
          {title}
        </CustomTypography>
      </Stack>

      {notifications == null ? (
        <Stack spacing={"12px"}>
          <Button
            fullWidth
            size={"lg"}
            variant="Soft"
            sx={addButtonStyle}
            startDecorator={<AddIcon size={20} />}
            disabled
          >
            {"추가"}
          </Button>
          <Stack px={1}>
            <CustomTypography
              variant={"Caption"}
              sx={{
                color: theme.vars.palette.Content.Weak,
              }}
            >
              {emptyMessage}
            </CustomTypography>
          </Stack>
        </Stack>
      ) : notifications.length > 0 ? (
        <>
          <Stack
            justifyContent="space-between"
            sx={{
              borderRadius: "16px",
              backgroundColor: theme.vars.palette.Surface.Forward,
              p: 1,
            }}
          >
            {notifications.map((notification, index) => (
              <Stack
                key={`${keyPrefix}-${index}`}
                direction={"row"}
                width={"100%"}
                height={"48px"}
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  px: "12px",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}
                onClick={() => handleNotificationClick(notification, index)}
              >
                <CustomTypography
                  variant={"Body"}
                  sx={{
                    fontWeight: 500,
                    color: theme.vars.palette.Content.Strong,
                  }}
                >
                  {formatTime(notification.time)}
                </CustomTypography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: 0,
                    color: theme.vars.palette.Content.Weak,
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "20px",
                  }}
                >
                  {formatDaysOfWeek(notification.dayOfWeekList)}
                  <RightArrowSvg color={theme.vars.palette.Content.SuperWeak} />
                </Box>
              </Stack>
            ))}
          </Stack>
          <Button
            fullWidth
            size={"lg"}
            variant="Soft"
            sx={addButtonStyle}
            startDecorator={<AddIcon size={20} />}
            onClick={handleAddClick}
          >
            {"추가"}
          </Button>
        </>
      ) : (
        // Case when record is an empty array
        <>
          <Button
            fullWidth
            size={"lg"}
            variant="Soft"
            sx={addButtonStyle}
            startDecorator={<AddIcon size={20} />}
            onClick={handleAddClick}
          >
            {"추가"}
          </Button>
        </>
      )}
    </Stack>
  );
};

export default function NotificationSettingsPage() {
  const user = useRecoilValue(userAtom);
  const deviceState = useRecoilValue(deviceAtom);
  const isInappWebview = useIsInappWebview();
  const navigate = useNavigate();
  const [notificationSettings, setNotificationSettings] = useState<
    NotificationSettingsDetailType | undefined
  >(undefined);
  const [clicked, setClicked] = useState(false);
  const [isGranted, setIsGranted] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    if (!isInappWebview) {
      return;
    }

    if (deviceState.pushNotificationStatus === undefined) {
      sendMessage(
        JSON.stringify({
          event: "get_pushNotification_status",
        })
      );
    } else if (deviceState.pushNotificationStatus) {
      setIsGranted(deviceState.pushNotificationStatus === "granted");
    }
  }, [deviceState.pushNotificationStatus, isInappWebview]);

  useQuery(
    [
      NOTIFICATION_SETTINGS_DOMAIN,
      NOTIFICATION_SETTINGS_DETAIL_ENDPOINT,
      user?.userId,
    ],
    () => notificationSettingsApi.getDetail(),
    {
      enabled: !!user?.userId,
      onSuccess: (data) => {
        if (data && data.result) {
          const notificationSettings = data.result;
          setNotificationSettings(notificationSettings);
        }
      },
    }
  );

  if (notificationSettings === undefined) {
    return <></>
  }

  return (
    <>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <TaskHeader
          key={`notification-settings_${user?.userId}`}
          taskKey={`notification-settings-${user?.userId}`}
          title={"알림"}
        />
      </Box>

      <ContentTemplateV2>
        {isInappWebview && isGranted === undefined ? (
          <></>
        ) : isInappWebview && isGranted !== undefined && !isGranted ? (
          <Box sx={{ height: "calc(100vh - 200px)" }}>
            <Stack
              alignItems={"center"}
              justifyContent={"center"}
              spacing={2}
              sx={{ height: "100%" }}
            >
              <CustomTypography
                variant={"Body"}
                sx={{
                  fontWeight: 500,
                  color: theme.vars.palette.Content.Medium,
                  textAlign: "center",
                  mb: 2,
                }}
              >
                {"알림 권한이 꺼져있어요.\n" +
                  "먼저 휴대전화 설정에서 알림을 허용해주세요."}
              </CustomTypography>
              <RequestPushNotificationButtonInSettings
                disabled={false}
                clicked={clicked}
                onButtonClick={() => setClicked(true)}
              />
            </Stack>
          </Box>
        ) : (
          <Stack spacing={4}>
            <NotificationSection
              title="생각기록지"
              notifications={notificationSettings?.thoughtRecord}
              emptyMessage="첫 번째 생각기록지를 작성한 후 알림을 설정할 수 있어요."
              keyPrefix="thought-record"
              type="thoughtRecord"
              navigate={navigate}
            />

            <NotificationSection
              title="명상훈련지"
              notifications={notificationSettings?.meditationRecord}
              emptyMessage="첫 번째 명상 훈련을 마친 후 알림을 설정할 수 있어요."
              keyPrefix="meditation-record"
              type="meditationRecord"
              navigate={navigate}
            />
          </Stack>
        )}
      </ContentTemplateV2>
    </>
  );
}
